
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useProfile } from "@/hooks/useProfile";
import { useWorkoutPlan } from "./useWorkoutPlan";
import { useHiddenRoutines } from "./useHiddenRoutines";
import { useOptimizedPlanRoutines } from "./useOptimizedPlanRoutines";
import { useGranularFieldUpdater } from "./useGranularFieldUpdater";
import { useRoutineActions } from "./useRoutineActions";

interface UseWorkoutPlanDataProps {
  planId?: string;
  initialPlan?: any;
}

export const useWorkoutPlanData = ({ planId: propPlanId, initialPlan }: UseWorkoutPlanDataProps) => {
  const { planId: paramPlanId } = useParams();
  const planId = propPlanId || paramPlanId;
  const { profile } = useProfile();
  
  // Only fetch if we don't have initialPlan (avoid duplicate queries)
  const shouldFetchPlan = !initialPlan;
  
  const { data: workoutPlanData, isLoading: isLoadingPlan, refetch: refetchPlan } = useWorkoutPlan(planId, { enabled: shouldFetchPlan });
  const { hiddenRoutines, isLoading: isLoadingHiddenRoutines } = useHiddenRoutines(planId);
  const { data: planRoutines, isLoading: isLoadingPlanRoutines, refetch: refetchPlanRoutines } = useOptimizedPlanRoutines(planId, { enabled: shouldFetchPlan });
  const { updateExerciseField, updateRoutineField } = useGranularFieldUpdater();
  
  // Use initialPlan if provided, otherwise use fetched data
  const workoutPlan = initialPlan || workoutPlanData;
  
  const [updateCounter, setUpdateCounter] = useState(0);
  
  // Optimized refetch system with navigation preservation
  const handleStructuralRefetch = async () => {
    console.log("🔄 Coordinated structural refetch (navigation preserving)");
    await Promise.all([refetchPlan(), refetchPlanRoutines()]);
    setUpdateCounter(prev => prev + 1);
  };
  
  // Enhanced minimal refetch for exercise operations that preserve navigation
  const handleMinimalRefetch = () => {
    console.log("🎯 Navigation-preserving minimal re-render trigger");
    setUpdateCounter(prev => prev + 1);
  };
  
  const { handleAddRoutine, handleRemoveRoutine } = useRoutineActions(planId, handleStructuralRefetch);

  const isLoading = isLoadingPlan || isLoadingHiddenRoutines || isLoadingPlanRoutines;
  
  // Use routines from initialPlan if available, otherwise from separate query
  const routinesData = initialPlan?.routines || planRoutines || [];

  // Filter routines to show
  let routinesToShow = routinesData;
  if (workoutPlan?.is_personal && workoutPlan.created_by === profile?.id) {
    routinesToShow = routinesToShow.filter(routine => !hiddenRoutines.includes(routine.id));
  }
  
  const canEdit = workoutPlan?.created_by === profile?.id || 
    (profile?.role === 'manager' && !workoutPlan?.is_personal);

  return {
    planId,
    profile,
    workoutPlan,
    personalRoutines: [], // Not needed here, removed useRoutines call
    planRoutines,
    routinesToShow,
    hiddenRoutines,
    isLoading,
    canEdit,
    updateCounter,
    updateExerciseField,
    handleStructuralRefetch,
    handleMinimalRefetch,
    handleAddRoutine,
    handleRemoveRoutine
  };
};
